// Cold treatment (USA) - the dialogs: start a lot, add pallets, place a probe, correct a reading, retire a probe.
// Every dialog only collects and checks input; the write is the caller's mutation.
import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { AlertTriangle } from 'lucide-react'
import Modal from '@/components/Modal'
import QueryStateGate from '@/components/QueryStateGate'
import { readState } from '@/lib/readState'
import { sanitizeTempInput } from '@/lib/coldroomTemps'
import {
  calibrationInDate, DEFAULT_FINAL_HOURS, DEFAULT_REQUIRED_HOURS, fmtC, MAX_REQUIRED_HOURS, MIN_FINAL_HOURS, MIN_REQUIRED_HOURS, parsePulp, parseThreshold,
  THRESHOLD_MAX, THRESHOLD_MIN, type ColdTreatmentProbe, type ColdTreatmentReading,
} from '@/lib/coldTreatment'
import {
  CT_KEYS, fetchCtEquipment, fetchCtRoomPallets, type CtEquipment, type CtRoom, type CtRoomPallet,
} from './coldTreatmentData'

const OUT_OF_DATE_SENTENCE = 'This probe has no calibration in date. Calibrate it under Compliance first.'

const LABEL = 'text-caption uppercase tracking-wider text-fg-muted'

/** The pallets of one room, tick to choose. Pallets already in the lot are not offered. */
function PalletPicker({ roomId, exclude, picked, setPicked }: {
  roomId: string; exclude: Set<string>; picked: Set<string>; setPicked: (s: Set<string>) => void
}) {
  const q = useQuery({ queryKey: CT_KEYS.roomPallets(roomId), queryFn: () => fetchCtRoomPallets(roomId), enabled: !!roomId })
  const rows: CtRoomPallet[] = useMemo(() => (q.data ?? []).filter(p => !exclude.has(p.pallet_id)), [q.data, exclude])
  if (!roomId) return <p className="text-caption text-fg-muted">Pick a room to see its pallets.</p>
  if (readState(q) !== 'usable') return <QueryStateGate query={q} noun="pallets in this room" empty={null} />
  if (rows.length === 0) return <p className="text-caption text-fg-muted">No pallets in this room are free to add.</p>
  const toggle = (id: string) => { const n = new Set(picked); if (n.has(id)) n.delete(id); else n.add(id); setPicked(n) }
  return (
    <ul className="max-h-56 overflow-y-auto space-y-1" aria-label="Pallets in the room">
      {rows.map(p => (
        <li key={p.pallet_id}>
          <label className="flex items-center gap-2 min-h-[44px] px-2 rounded-lg bg-surface text-sm">
            <input type="checkbox" checked={picked.has(p.pallet_id)} onChange={() => toggle(p.pallet_id)} className="rounded" />
            <span className="font-mono text-caption">{p.pallet_id}</span>
            <span className="text-caption text-fg-muted truncate">
              {[p.variety, p.coldroom_grid, p.carton_count != null ? `${p.carton_count} ctns` : null].filter(Boolean).join(' · ')}
            </span>
          </label>
        </li>
      ))}
    </ul>
  )
}

export interface StartLotVars {
  coldroomId: string; threshold: number; requiredHours: number; finalHours: number; note: string | null; palletIds: string[]
}

interface StartLotForm { roomId: string; threshold: string; reqH: string; finH: string; note: string; picked: Set<string> }

function checkHours(rh: number, fh: number): string | null {
  if (!Number.isInteger(rh) || rh < MIN_REQUIRED_HOURS || rh > MAX_REQUIRED_HOURS) return `Required hours must be a whole number from ${MIN_REQUIRED_HOURS} to ${MAX_REQUIRED_HOURS}.`
  if (!Number.isInteger(fh) || fh < MIN_FINAL_HOURS || fh > rh) return `Final hours must be a whole number from ${MIN_FINAL_HOURS} up to the required hours.`
  return null
}

// The first problem with the form as a sentence, or the values to save. Nothing is saved on a problem.
function checkStartLot(f: StartLotForm): { err: string | null; vars?: StartLotVars } {
  const th = parseThreshold(f.threshold)
  const rh = Number(f.reqH), fh = Number(f.finH)
  if (!f.roomId) return { err: 'Pick a room.' }
  if (th === null) return { err: `Type the protocol threshold in °C, between ${THRESHOLD_MIN} and ${THRESHOLD_MAX}, for example -0.55.` }
  const hoursErr = checkHours(rh, fh)
  if (hoursErr) return { err: hoursErr }
  if (f.picked.size === 0) return { err: 'Pick at least one pallet.' }
  return { err: null, vars: { coldroomId: f.roomId, threshold: th, requiredHours: rh, finalHours: fh, note: f.note.trim() || null, palletIds: [...f.picked] } }
}

export function StartLotModal({ rooms, isPending, onClose, onSave }: {
  rooms: CtRoom[]; isPending: boolean; onClose: () => void; onSave: (v: StartLotVars) => void
}) {
  // USDA-approved rooms first.
  const ordered = useMemo(() => rooms.filter(r => r.is_active).sort((a, b) => Number(b.usda_approved) - Number(a.usda_approved)), [rooms])
  const [roomId, setRoomId] = useState(ordered[0]?.coldroom_id ?? '')
  const [threshold, setThreshold] = useState('')
  const [reqH, setReqH] = useState(String(DEFAULT_REQUIRED_HOURS))
  const [finH, setFinH] = useState(String(DEFAULT_FINAL_HOURS))
  const [note, setNote] = useState('')
  const [picked, setPicked] = useState<Set<string>>(new Set())
  const [err, setErr] = useState<string | null>(null)
  const room = ordered.find(r => r.coldroom_id === roomId)
  const none = new Set<string>()

  function submit() {
    const r = checkStartLot({ roomId, threshold, reqH, finH, note, picked })
    setErr(r.err)
    if (r.vars) onSave(r.vars)
  }

  return (
    <Modal title="Start a cold treatment lot" subtitle="The start time is set by the server when you save."
      onClose={onClose} snapshot={`${roomId}|${threshold}|${reqH}|${finH}|${note}|${[...picked].join(',')}`}
      isPending={isPending} submitLabel="Start lot" onSubmit={submit}>
      <div className="space-y-3">
        <label className="block">
          <span className={LABEL}>Room</span>
          <select value={roomId} onChange={e => { setRoomId(e.target.value); setPicked(new Set()) }} className="field text-base min-h-[44px] mt-0.5">
            {ordered.map(r => <option key={r.coldroom_id} value={r.coldroom_id}>{r.code} · {r.name}{r.usda_approved ? ' · USDA-approved' : ''}</option>)}
          </select>
        </label>
        {room && !room.usda_approved && (
          <p role="alert" className="flex items-start gap-1.5 text-caption text-warning">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" aria-hidden="true" />
            This room is not marked USDA-approved. USDA may refuse the treatment. Tick the room under Config → Storage if it is approved.
          </p>
        )}
        <label className="block">
          <span className={LABEL}>Protocol threshold, °C (at or below)</span>
          <input value={threshold} inputMode="decimal" placeholder="-0.55" onChange={e => setThreshold(sanitizeTempInput(e.target.value))}
            className="field text-base min-h-[44px] mt-0.5 tabular-nums" />
        </label>
        <div className="grid grid-cols-2 gap-2">
          <label className="block">
            <span className={LABEL}>Total hours</span>
            <input value={reqH} inputMode="numeric" onChange={e => setReqH(e.target.value.replace(/\D/g, ''))} className="field text-base min-h-[44px] mt-0.5 tabular-nums" />
          </label>
          <label className="block">
            <span className={LABEL}>Last hours at or below</span>
            <input value={finH} inputMode="numeric" onChange={e => setFinH(e.target.value.replace(/\D/g, ''))} className="field text-base min-h-[44px] mt-0.5 tabular-nums" />
          </label>
        </div>
        <label className="block">
          <span className={LABEL}>Note (optional)</span>
          <input value={note} maxLength={500} onChange={e => setNote(e.target.value)} className="field text-base min-h-[44px] mt-0.5" />
        </label>
        <div>
          <p className={LABEL}>Pallets ({picked.size} picked)</p>
          <PalletPicker roomId={roomId} exclude={none} picked={picked} setPicked={setPicked} />
        </div>
        {err && <p role="alert" className="text-caption text-danger">{err}</p>}
      </div>
    </Modal>
  )
}

export function AddPalletsModal({ roomId, already, isPending, onClose, onSave }: {
  roomId: string; already: string[]; isPending: boolean; onClose: () => void; onSave: (ids: string[]) => void
}) {
  const [picked, setPicked] = useState<Set<string>>(new Set())
  const [err, setErr] = useState<string | null>(null)
  const exclude = useMemo(() => new Set(already), [already])
  return (
    <Modal title="Add pallets" subtitle="Pallets are added, never removed. A lot with a wrong pallet is replaced by a new lot."
      onClose={onClose} snapshot={[...picked].join(',')} isPending={isPending} submitLabel="Add pallets"
      onSubmit={() => { if (picked.size === 0) { setErr('Pick at least one pallet.'); return } setErr(null); onSave([...picked]) }}>
      <PalletPicker roomId={roomId} exclude={exclude} picked={picked} setPicked={setPicked} />
      {err && <p role="alert" className="text-caption text-danger mt-2">{err}</p>}
    </Modal>
  )
}

export interface PlaceProbeVars { equipmentId: string; palletId: string; position: string }

export function PlaceProbeModal({ pallets, usedEquipment, isPending, onClose, onSave }: {
  pallets: string[]; usedEquipment: Set<string>; isPending: boolean; onClose: () => void; onSave: (v: PlaceProbeVars) => void
}) {
  const q = useQuery({ queryKey: CT_KEYS.equipment, queryFn: fetchCtEquipment })
  const [equipmentId, setEquipmentId] = useState('')
  const [palletId, setPalletId] = useState(pallets[0] ?? '')
  const [position, setPosition] = useState('')
  const [err, setErr] = useState<string | null>(null)
  const items: CtEquipment[] = useMemo(() => (q.data ?? []).filter(e => !usedEquipment.has(e.equipment_id)), [q.data, usedEquipment])
  const chosen = items.find(e => e.equipment_id === equipmentId)

  function submit() {
    if (!chosen) return setErr('Pick the probe.')
    if (!calibrationInDate(chosen)) return setErr(OUT_OF_DATE_SENTENCE)
    if (!palletId) return setErr('Pick the pallet the probe sits in.')
    if (!position.trim()) return setErr('Say where in the pallet the probe sits, for example core, middle layer.')
    setErr(null)
    onSave({ equipmentId: chosen.equipment_id, palletId, position: position.trim() })
  }

  return (
    <Modal title="Place a probe" subtitle="A probe is an equipment item with a calibration in date."
      onClose={onClose} snapshot={`${equipmentId}|${palletId}|${position}`} isPending={isPending} submitLabel="Place probe" onSubmit={submit}>
      <div className="space-y-3">
        {readState(q) !== 'usable' ? <QueryStateGate query={q} noun="equipment" empty={null} /> : (
          <label className="block">
            <span className={LABEL}>Probe</span>
            <select value={equipmentId} onChange={e => setEquipmentId(e.target.value)} className="field text-base min-h-[44px] mt-0.5">
              <option value="">Pick a probe</option>
              {items.map(e => (
                <option key={e.equipment_id} value={e.equipment_id}>
                  {e.asset_tag} · {e.name}{calibrationInDate(e) ? ` · calibrated to ${e.next_calibration_due}` : ' · calibration out of date'}
                </option>
              ))}
            </select>
          </label>
        )}
        {chosen && !calibrationInDate(chosen) && <p role="alert" className="text-caption text-warning">{OUT_OF_DATE_SENTENCE}</p>}
        <label className="block">
          <span className={LABEL}>Pallet</span>
          <select value={palletId} onChange={e => setPalletId(e.target.value)} className="field text-base min-h-[44px] mt-0.5">
            {pallets.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </label>
        <label className="block">
          <span className={LABEL}>Position in the pallet</span>
          <input value={position} maxLength={200} placeholder="core, middle layer" onChange={e => setPosition(e.target.value)}
            className="field text-base min-h-[44px] mt-0.5" />
        </label>
        {err && <p role="alert" className="text-caption text-danger">{err}</p>}
      </div>
    </Modal>
  )
}

export function CorrectReadingModal({ reading, probe, isPending, onClose, onSave }: {
  reading: ColdTreatmentReading; probe: ColdTreatmentProbe | undefined; isPending: boolean
  onClose: () => void; onSave: (v: { pulp: number; note: string | null }) => void
}) {
  const [pulp, setPulp] = useState(String(reading.pulp_temp_c))
  const [note, setNote] = useState('')
  const [err, setErr] = useState<string | null>(null)
  return (
    <Modal title="Correct reading" subtitle={`Probe ${probe?.probe_no ?? ''} · was ${fmtC(reading.pulp_temp_c)} °C`}
      onClose={onClose} snapshot={`${pulp}|${note}`} isPending={isPending} submitLabel="Save correction"
      onSubmit={() => {
        const p = parsePulp(pulp)
        if (p === null) { setErr('Type the pulp temperature in °C, for example -0.7.'); return }
        setErr(null); onSave({ pulp: p, note: note.trim() || null })
      }}>
      <p className="text-caption text-fg-muted mb-2">The old reading stays on the record, struck through. Nothing is deleted.</p>
      <label className="block">
        <span className={LABEL}>Pulp °C</span>
        <input value={pulp} inputMode="decimal" autoFocus onChange={e => setPulp(sanitizeTempInput(e.target.value))} className="field text-base min-h-[44px] mt-0.5 tabular-nums" />
      </label>
      <label className="block mt-2">
        <span className={LABEL}>Why (optional)</span>
        <input value={note} maxLength={500} onChange={e => setNote(e.target.value)} className="field text-base min-h-[44px] mt-0.5" />
      </label>
      {err && <p role="alert" className="text-caption text-danger mt-2">{err}</p>}
    </Modal>
  )
}

export function RetireProbeModal({ probe, isPending, onClose, onSave }: {
  probe: ColdTreatmentProbe; isPending: boolean; onClose: () => void; onSave: (reason: string) => void
}) {
  const [reason, setReason] = useState('')
  const [err, setErr] = useState<string | null>(null)
  return (
    <Modal title={`Retire probe ${probe.probe_no}`} subtitle="Use this when a probe was placed wrongly. Its readings stay on the record."
      onClose={onClose} snapshot={reason} isPending={isPending} submitLabel="Retire probe"
      onSubmit={() => { if (!reason.trim()) { setErr('Say why the probe is retired.'); return } setErr(null); onSave(reason.trim()) }}>
      <label className="block">
        <span className={LABEL}>Why</span>
        <input value={reason} maxLength={500} autoFocus onChange={e => setReason(e.target.value)} className="field text-base min-h-[44px] mt-0.5" />
      </label>
      {err && <p role="alert" className="text-caption text-danger mt-2">{err}</p>}
    </Modal>
  )
}
